import { ConfigStateService, CoreModule, provideAbpCore, withOptions } from '@abp/ng.core';
import { registerLocale } from '@abp/ng.core/locale';
import { provideFeatureManagementConfig } from '@abp/ng.feature-management';
import { provideAbpOAuth } from '@abp/ng.oauth';
import { provideIdentityConfig } from '@abp/ng.identity/config';
import { provideSettingManagementConfig } from '@abp/ng.setting-management/config';
import { provideTenantManagementConfig } from '@abp/ng.tenant-management/config';
import { provideAccountConfig } from '@abp/ng.account/config';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { OAuthService, OAuthStorage } from 'angular-oauth2-oidc';

export function storageFactory(): OAuthStorage {
  return localStorage;
}
import { ThemeLeptonXModule } from '@abp/ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@abp/ng.theme.lepton-x/layouts';
import { AccountLayoutModule } from '@abp/ng.theme.lepton-x/account';
import { ThemeSharedModule, withValidationBluePrint, provideAbpThemeShared, ConfirmationService } from '@abp/ng.theme.shared';
import { ServiceWorkerModule } from '@angular/service-worker';
import { SharedModule } from './shared/shared.module';
import { WineriesComponent } from './wineries/wineries.component';
import { WineryFormComponent } from './wineries/create-edit-winery/winery-form.component';
import { SommeliersComponent } from './sommeliers/sommeliers.component';
import { SommelierFormComponent } from './sommeliers/create-edit-sommelier/sommelier-form.component';
import { WinesComponent } from './wines/wines.component';
import { WineFormComponent } from './wines/create-edit-wine/wine-form.component';
import { CouponsComponent } from './coupons/coupons.component';
import { CouponFormComponent } from './coupons/create-edit-coupon/coupon-form.component';
import { CommunicationsComponent } from './communications/communications.component';
import { CommunicationFormComponent } from './communications/create-edit-communication/communication-form.component';

import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { LoginComponent } from './login/login.component';
import { CatalogComponent } from './catalog/catalog.component';
import { OrdersComponent } from './orders/orders.component';
import { ProfileComponent } from './profile/profile.component';
import { ShoppingCartComponent } from './shopping-cart/shopping-cart.component';
import { BackofficeOrdersComponent } from './backoffice-orders/backoffice-orders.component';
import { ConfirmationModalService } from './shared/services/confirmation-modal.service';
import { SelectedSommelierService } from './shared/services/selected-sommelier.service';
import { SommelierService } from '@proxy/sommeliers';
import { ClientService } from '@proxy/clients';
import { SommelierProfileComponent } from './sommelier-profile/sommelier-profile.component';
import { OrderDetailComponent } from './backoffice-orders/order-detail/order-detail.component';
import { SettlementCommissionsComponent } from './reports/settlement-commissions.component';
import { ForgotPasswordCustomComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordCustomComponent } from './reset-password/reset-password.component';
import { PickupPointsComponent } from './pickuppoints/pickuppoints.component';
import { PickupFormComponent } from './pickuppoints/create-edit-pickuppoint/pickup-form.component';
import { OrderDetailsComponent } from './order/order-details.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { SommelierOrdersComponent } from './sommelier-orders/sommelier-orders.component';
import { ColppyHealthCheckComponent } from './colppy-healthcheck/colppy-healthcheck.component';
import { WineSalesReportComponent } from './reports/wine-sales-report/wine-sales-report.component';
import { ClientsReportComponent } from './reports/clients-report/clients-report.component';
import { CreateOrderComponent } from './backoffice-orders/create-order/create-order.component';
import { MyNumbersComponent } from './my-numbers/my-numbers.component';
import { SommelierClientsComponent } from './sommelier-clients/sommelier-clients.component';
import { DeliveriesComponent } from './deliveries/deliveries.component';
import { MySommelierComponent } from './my-sommelier/my-sommelier.component';
import { AdminClientSommelierComponent } from './admin-client-sommelier/admin-client-sommelier.component';
import { DeliverOrderFormComponent } from './deliveries/deliver-order-form/deliver-order-form.component';
import { ClientDetailsComponent } from './sommelier-clients/client-details/client-details.component';
import { AssistedCartComponent } from './sommelier-clients/assisted-cart/assisted-cart.component';
import { AssistedCatalogComponent } from './sommelier-clients/assisted-catalog/assisted-catalog.component';
import { AssistedWineDetailsComponent } from './sommelier-clients/assisted-wine-details/assisted-wine-details.component';
import { AssistedOrderDetailsComponent } from './sommelier-clients/assisted-order-details/assisted-order-details.component';
import { GoogleCallbackComponent } from './auth/google-callback.component';

export function initSlug(selectedService: SelectedSommelierService) {
  return () => {
    const saved = localStorage.getItem('selectedSlug');
    if (saved) {
      selectedService.setSlug(saved);
    }
  };
}

export function isLoginRedirect(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  return (
    hash.includes('access_token=') ||
    hash.includes('id_token=') ||
    hash.includes('error=') ||
    search.includes('error=')
  );
}

export function initializeAppAuth(oauthService: OAuthService) {
  return () => {
    const config = environment.oAuthConfig;
    if (config) {
      oauthService.configure(config);
      oauthService.setupAutomaticSilentRefresh();
    }

    if (isLoginRedirect()) {
      return oauthService.loadDiscoveryDocumentAndTryLogin()
        .then(() => Promise.resolve())
        .catch(() => Promise.resolve());
    }

    return oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (!oauthService.hasValidAccessToken() && oauthService.getRefreshToken()) {
        return oauthService.refreshToken().then(() => {
        }).catch(() => {
        });
      }
      return Promise.resolve();
    }).catch(() => {
    });
  };
}


@NgModule({
  imports: [
    SharedModule,
    ReactiveFormsModule,
    FormsModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    ThemeLeptonXModule.forRoot(),
    SideMenuLayoutModule.forRoot(),
    AccountLayoutModule.forRoot(),
    ThemeSharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  declarations: [
    AppComponent,
    LoginComponent,
    ForgotPasswordCustomComponent,
    ResetPasswordCustomComponent,
    WineriesComponent,
    WineryFormComponent,
    SommeliersComponent,
    SommelierFormComponent,
    WinesComponent,
    WineFormComponent,
    CatalogComponent,
    OrdersComponent,
    ProfileComponent,
    ShoppingCartComponent,
    BackofficeOrdersComponent,
    SommelierProfileComponent,
    OrderDetailComponent,
    SettlementCommissionsComponent,
    PickupPointsComponent,
    PickupFormComponent,
    OrderDetailsComponent,
    ConfirmEmailComponent,
    SommelierOrdersComponent,
    ColppyHealthCheckComponent,
    WineSalesReportComponent,
    ClientsReportComponent,
    CreateOrderComponent,
    MyNumbersComponent,
    SommelierClientsComponent,
    DeliveriesComponent,
    MySommelierComponent,
    AdminClientSommelierComponent,
    DeliverOrderFormComponent,
    CouponsComponent,
    CouponFormComponent,
    CommunicationsComponent,
    CommunicationFormComponent,
    ClientDetailsComponent,
    AssistedCartComponent,
    AssistedCatalogComponent,
    AssistedWineDetailsComponent,
    AssistedOrderDetailsComponent,
    GoogleCallbackComponent
  ],
  providers: [
    APP_ROUTE_PROVIDER,
    { provide: OAuthStorage, useFactory: storageFactory },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppAuth,
      deps: [OAuthService],
      multi: true
    },
    provideAbpCore(withOptions({
      environment,
      registerLocaleFn: registerLocale(),
    })),
    provideAbpOAuth(),
    provideSettingManagementConfig(),
    provideAccountConfig(),
    provideIdentityConfig(),
    provideTenantManagementConfig(),
    provideFeatureManagementConfig(),
    provideAbpThemeShared(withValidationBluePrint({
      wrongPassword: 'Please choose 1q2w3E*'
    })),
    {
      provide: ConfirmationService,
      useClass: ConfirmationModalService
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initSlug,
      deps: [SelectedSommelierService, SommelierService, ClientService, ConfigStateService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
}
